// Legal aid status on the billing sheet: what the LACW Aid Status Diary used to hold.
//
// The diary and the billing sheet read the same weekly diary, so the week is now uploaded
// once, here. Each appearance carries an aid entry beside its billing action:
//   { aidStatus, aidNumber, confirmed, confirmedBy, confirmedAt, aidPlannerFill, grantActionLodged }
// kept in the week's `aid` map by appearance id. The Aid Planner fills it in (as it filled
// the diary) and the administrator checks and confirms it.
//
// The aid status is what is true of the grant; the billing action is what to do with the
// claim. They are kept apart on purpose: setting an action can move a matter onto a held
// list or open the extension email, which nobody should get without choosing it. When the
// two disagree the row says so (aidActionConflict).
//
// LACW see the aid status, not the billing. publishedAidWeek and publishedHeldLists are
// the only shapes that leave this screen for them: no fees, actions or notes.

import { diarySuggestions, fillDiaryFromAidPlanner } from "./diaryAidMatch.js";
import { isClaimedAction } from "./billingConstants.js";

export const AID_STATUSES = [
  "Not checked",
  "Aid confirmed",
  "LACW funded",
  "Aid pending",
  "Aid not applied for",
  "Extension required",
  "Private / other funding",
  "Aid not required",
];

export const AID_STATUS_STYLE = {
  "Not checked": "border-slate-200 bg-slate-50 text-slate-500",
  "Aid confirmed": "border-emerald-300 bg-emerald-50 text-emerald-800",
  "LACW funded": "border-purple-300 bg-purple-50 text-purple-800",
  "Aid pending": "border-amber-300 bg-amber-50 text-amber-800",
  "Aid not applied for": "border-red-300 bg-red-50 text-red-800",
  "Extension required": "border-orange-300 bg-orange-50 text-orange-800",
  "Private / other funding": "border-sky-300 bg-sky-50 text-sky-800",
  "Aid not required": "border-slate-300 bg-slate-50 text-slate-700",
};

export const EMPTY_AID = Object.freeze({ aidStatus: "Not checked", aidNumber: "", confirmed: false });

export function aidFor(aid = {}, id) {
  return { ...EMPTY_AID, ...(aid?.[id] || {}) };
}

// A billing appearance in the shape the diary helpers (matching, reminders) read.
export function diaryRow(entry, aid = {}) {
  const row = aidFor(aid, entry.id);
  return {
    id: entry.id,
    date: entry.date || "",
    clientName: entry.client_name || "",
    court: entry.court || "",
    appearanceType: entry.appearance_type || "",
    lawyer: String(entry.lawyer_initials || "").toUpperCase(),
    grantType: String(entry.grant_type || "").toUpperCase(),
    ...row,
  };
}

export function diaryRows(entries = [], aid = {}) {
  return (Array.isArray(entries) ? entries : []).map((entry) => diaryRow(entry, aid));
}

// What the Aid Planner says about each appearance, by appearance id.
export function aidSuggestions(entries = [], aid = {}, matters = [], options = {}) {
  return diarySuggestions(diaryRows(entries, aid), matters, options);
}

// The aid map with the Aid Planner's status filled in where nobody has looked yet
// ("Not checked" and unconfirmed). Returns the same object when nothing changes, so it is
// safe to run from an effect.
export function fillAidFromPlanner(entries = [], aid = {}, suggestions = new Map(), now = new Date()) {
  const rows = diaryRows(entries, aid);
  const filled = fillDiaryFromAidPlanner(rows, suggestions, now);
  if (filled === rows) return aid;
  const next = { ...aid };
  filled.forEach((row, index) => {
    if (row === rows[index]) return;
    next[row.id] = {
      ...aidFor(aid, row.id),
      aidStatus: row.aidStatus,
      aidNumber: row.aidNumber || "",
      aidPlannerFill: row.aidPlannerFill,
    };
  });
  return next;
}

// What stops an appearance's aid status being confirmed, if anything.
export function confirmBlocker(aid = EMPTY_AID) {
  if (!aid.aidStatus || aid.aidStatus === "Not checked") return "Choose an aid status first.";
  if (aid.aidStatus === "Aid confirmed" && !String(aid.aidNumber || "").trim()) return "Enter the aid number first.";
  return "";
}

// Using a suggestion by hand: the status changes, so any earlier confirmation no longer holds.
export function applySuggestion(current = EMPTY_AID, suggestion, now = new Date()) {
  return {
    ...current,
    aidStatus: suggestion.status,
    aidNumber: suggestion.status === "Aid confirmed" ? suggestion.aidNumber || "" : "",
    confirmed: false,
    confirmedBy: "",
    confirmedAt: "",
    aidPlannerFill: { matterId: suggestion.matterId, status: suggestion.status, reason: suggestion.reason || "", at: now.toISOString() },
  };
}

// The aid status says the claim cannot be made yet, but the row is set to claim it.
const NOT_CLAIMABLE_YET = {
  "Extension required": "needs an extension",
  "Aid not applied for": "has no grant in place",
  "Aid pending": "is still waiting on an aid decision",
};

export function aidActionConflict(aidStatus, action) {
  const why = NOT_CLAIMABLE_YET[aidStatus];
  if (!why || !isClaimedAction(action)) return "";
  return `Set to claim, but the aid status says this matter ${why}.`;
}

export function aidStats(entries = [], aid = {}) {
  const rows = diaryRows(entries, aid);
  return {
    total: rows.length,
    confirmed: rows.filter((row) => row.confirmed).length,
    funded: rows.filter((row) => row.aidStatus === "Aid confirmed" || row.aidStatus === "LACW funded").length,
    pending: rows.filter((row) => row.aidStatus === "Aid pending").length,
  };
}

export const AID_FILTERS = ["All", "Needs confirmation", ...AID_STATUSES];

export function matchesAidFilter(row, filter = "All") {
  if (filter === "All") return true;
  if (filter === "Needs confirmation") return !row.confirmed;
  return row.aidStatus === filter;
}

// --- what LACW see -------------------------------------------------------------------------

export const AID_WEEK_FORM_TYPE = "lacw_aid_week";
// Published weeks are kept 12 months (api/_draft-schema.js RETENTION_MONTHS). A week whose
// appearances are all older than that is not published again when it is reopened, or it
// would come back for another year.
export const AID_WEEK_RETENTION_MONTHS = 12;

function appearanceTime(value = "") {
  const match = String(value).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  return match ? new Date(+match[3], +match[2] - 1, +match[1]).getTime() : null;
}

export function weekPastRetention(entries = [], now = new Date()) {
  const times = (Array.isArray(entries) ? entries : []).map((entry) => appearanceTime(entry?.date)).filter((time) => time != null);
  if (!times.length) return false;
  const cutoff = new Date(now);
  cutoff.setMonth(cutoff.getMonth() - AID_WEEK_RETENTION_MONTHS);
  return Math.max(...times) < cutoff.getTime();
}
export const HELD_LISTS_FORM_TYPE = "lacw_held_lists";
// One shared record of the held lists for LACW, so every save replaces the same row.
export const HELD_LISTS_DRAFT_ID = "00000000-0000-4000-8000-00000000a1d1";

// Only these fields of an appearance are shown to LACW.
export function publishedAidWeek(entries = [], aid = {}, weekLabel = "") {
  return {
    weekLabel,
    publishedAt: new Date().toISOString(),
    rows: diaryRows(entries, aid).map((row) => ({
      id: row.id,
      date: row.date,
      clientName: row.clientName,
      court: row.court,
      appearanceType: row.appearanceType,
      lawyer: row.lawyer,
      grantType: row.grantType,
      aidStatus: row.aidStatus,
      aidNumber: row.aidNumber || "",
      confirmed: Boolean(row.confirmed),
      confirmedAt: row.confirmedAt || "",
      ...(row.aidPlannerFill?.reason ? { reason: row.aidPlannerFill.reason } : {}),
      ...(row.grantActionLodged ? { grantActionLodged: true } : {}),
    })),
  };
}

const HELD_LIST_TITLES = {
  "Aid pending": "Aid Pending",
  "To be claimed": "To be claimed",
  "Counsel appeared": "Awaiting fee slip",
};

// The held lists without their notes or fee slip details. A matter ticked off Awaiting fee
// slip is no longer waiting, so it is left out.
export function publishedHeldLists(items = []) {
  return {
    publishedAt: new Date().toISOString(),
    items: (Array.isArray(items) ? items : [])
      .filter((item) => HELD_LIST_TITLES[item?.action])
      .filter((item) => !(item.action === "Counsel appeared" && item.feeSlipDoneAt))
      .map((item) => ({
        list: HELD_LIST_TITLES[item.action],
        date: item.date || "",
        clientName: item.client_name || "",
        court: item.court || "",
        appearanceType: item.appearance_type || "",
        lawyer: String(item.lawyer_initials || "").toUpperCase(),
        since: item.pendingSince || "",
      })),
  };
}

export const HELD_LIST_ORDER = Object.values(HELD_LIST_TITLES);
